import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  AutoMinerDef,
  EquipmentInstance,
  EquipmentSlot,
  EquippedMap,
  ItemDef,
  Player,
  ResourceId,
  SkillId,
} from '@/domain/types';
import { addXp, createPlayer, migrateToV2, randomId, touch } from '@/domain/player';
import { addCredits, spendCredits, addCorruptedData, spendCorruptedData, addCleanCode, spendCleanCode, spendNeonGems, addNeonGems } from '@/domain/currency';
// Decrypter network was removed in P2.A.5 sunset — kept import-comment as a
// marker for any forgotten reference. CD now feeds Augment-Tree directly.
import { addItem, hasItem, removeItem, withInventory } from '@/domain/inventory';
import { getItem, ITEMS } from '@/domain/items';
import { equipInstance as doEquip, unequipSlot, replaceInstance, findInstance, removeFromStash } from '@/domain/equipment';
// calcPlayerStats / getEquippedStats were used only by the removed
// combatSiphonTap path. Combat-stat-calc now happens in CombatViewStore3D.
import { instanceStats, upgradeCost, canAfford, spendCost, maxLevel } from '@/domain/upgrade';
import { accrueOfflineYield, minerCost, MINERS, ownedCount } from '@/domain/autoMiner';
import { activateBoost, filterActive, getBoost } from '@/domain/boosts';
import { hasSetBonus } from '@/domain/itemSets';
import { hasSkill, activeSkillValue, skillsUnlockedByTier } from '@/domain/skills';
import { RESEARCH_NODES, researchBonus } from '@/domain/research';
import { getSkillWebNode, isNodeAvailable, nodeLevel, SKILL_WEB_NODES } from '@/domain/skillWeb';
import {
  ACHIEVEMENTS, achievementsByTrigger,
} from '@/domain/achievements';
import { COSMETICS } from '@/domain/cosmetics';
import { DAILY_QUESTS, nextResetAt, pickDailyQuest } from '@/domain/dailyQuests';
import { TUTORIAL_DONE } from '@/domain/tutorial';
import { AUGMENT_BRANCHES, augmentCost, emptyAugments, totalAugmentValue, type AugmentBranch } from '@/domain/augments';
import {
  ASPECTS, aspectLevelUpCost, defaultAspectsState,
  type AspectId, type AspectSlot, type AspectActiveSkillId,
} from '@/domain/aspects';
import { getVault } from '@/domain/vaults';
import { getEnemy } from '@/domain/enemies';
import { rollTierLoot, calcScaledHpMult, rollBonusStats } from '@/state/dungeonStore';

export interface SiphonTickResult {
  credits: number;
  itemDropped: ItemDef | null;
}

interface GameState {
  player: Player | null;

  createNewPlayer: (name: string) => void;
  resetPlayer: () => void;

  grantCredits: (amount: number) => void;
  tryBuy: (amount: number) => boolean;
  grantXp: (amount: number) => void;

  /** Soft-pity for legendary drops. Called at end-of-run by dungeonStore.
   *  - droppedLegendary=true → reset pity to 0
   *  - else                   → bump pity by +0.005, capped at 0.30 (=30% extra
   *                             chance on legendary rolls). */
  bumpLegendaryPity: (droppedLegendary: boolean) => void;

  /** Endless-Mode highscore. Called by dungeonStore on flee/defeat/victory of
   *  an endless run with the highest wave the player REACHED (entered).
   *  No-op when the new score is lower than the existing record. */
  recordEndlessWave: (wave: number) => void;

  /** Reset all Skill-Web-Nodes and refund their full investment (cleanCode +
   *  nano_chip + data_track). First respec is free; subsequent ones cost
   *  1000 Corrupted Data upfront. Returns true on success. Interim feature
   *  before the Skill-Web sunset in P2.B — gives players a way to undo bad
   *  picks without rerolling characters. */
  respecSkillWeb: () => boolean;

  // ── Retention event bus (P1.4) ──────────────────────────────
  /** Central event sink — every gameplay action that can move an
   *  achievement / daily-quest forward fires this. Updates both at once.
   *  Newly-unlocked achievements queue their toasts via `pendingToasts`. */
  bumpEvent: (trigger: import('@/domain/achievements').AchievementTrigger, amount?: number) => void;
  /** Claim the reward of an unlocked achievement (Neon-Gems + optional
   *  cosmetic). No-op if already claimed or not yet unlocked. */
  claimAchievement: (id: string) => boolean;
  /** Claim today's completed daily quest. Pays out CD + Ascension-Fragments. */
  claimDailyQuest: () => boolean;
  /** Ensure player has today's quest assigned. Called on app boot. */
  refreshDailyQuest: () => void;
  /** Per-render-cycle toast queue. Components shift items off when shown. */
  pendingToasts: Array<{ id: string; kind: 'achievement'; achId: string }>;
  dismissToast: (id: string) => void;
  /** Equip/unequip a cosmetic. Mutually exclusive per category. */
  setCosmeticEquipped: (cosmeticId: string, equipped: boolean) => boolean;
  /** Buy a cosmetic with Neon-Gems. Returns true on success. */
  buyCosmetic: (cosmeticId: string) => boolean;

  /** Move the tutorial forward to (at least) the given step. Idempotent —
   *  never moves backwards, so trigger sites can fire on every event safely. */
  advanceTutorial: (toStep: number) => void;
  /** Player taps "Tutorial überspringen" → jump to TUTORIAL_DONE. */
  skipTutorial: () => void;

  /** Restore the player to the pre-migration snapshot saved by the persist
   *  middleware. Returns false if no backup exists or it has expired.
   *  Support-tool only — exposed for the case where a migration introduces
   *  a regression and a user needs their pre-update state back. */
  rollbackToBackup: () => boolean;

  /** P2.A Augment-Tree: purchase the NEXT node in a given branch. Pays the
   *  position-doubled CD cost (500 → 8000). Returns false on insufficient
   *  funds or branch already maxed. Nodes are sequential — branch.nodes[i]
   *  unlocks only after branch.nodes[i-1] is owned. */
  buyAugmentNode: (branch: AugmentBranch) => boolean;

  /** P2.B Aspekt-System: slot an aspect into primary/secondary. Returns
   *  false if the aspect isn't unlocked or the slot would create a duplicate
   *  (both slots same aspect). Setting `null` unequips the secondary slot. */
  setAspect: (slot: AspectSlot, aspectId: AspectId | null) => boolean;
  /** Spend CD to bump an aspect's level by 1 (max 5). First-slot unlock
   *  grants L1 free; subsequent levels cost 2.5k → 70k CD per spec. */
  investAspectLevel: (aspectId: AspectId) => boolean;
  /** Equip an unlocked active-skill into the player's slot. Pool is
   *  populated automatically when an aspect is first slotted (its active
   *  joins `unlockedActiveSkills`). */
  setActiveSkill: (skillId: AspectActiveSkillId | null) => boolean;

  giveItem: (item: ItemDef, quantity?: number) => number;
  takeItem: (itemId: string, quantity?: number) => boolean;

  equipInstance: (instanceId: string) => boolean;
  unequipSlot: (slot: EquipmentSlot) => boolean;
  upgradeInstance: (instanceId: string) => { ok: boolean; newLevel?: number };
  /** P2.C Bonus-Stat-Reforge: spend 5× nano_chip + 1× data_track to re-roll
   *  the item's affix lines (rarity-respecting). Returns the new bonusStats
   *  array on success, null on insufficient resources / missing item. The
   *  caller (UI) can decide whether to display "yes accept" / "no try again"
   *  flows around this. */
  reforgeBonusStats: (instanceId: string) => { ok: boolean; newStats?: import('@/domain/types').BonusStat[] };
  salvageInstance: (instanceId: string) => boolean;
  salvageStashByRarity: (rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'mythic' | 'all') => number;
  ascendInstance: (instanceId: string) => boolean;

  buyMiner: (id: string) => boolean;
  tickMiners: () => Partial<Record<ResourceId | 'credits', number>>;

  // Decrypter + Heat APIs removed in P2.A.5 sunset. CD now feeds the
  // Augment-Tree directly (buyAugmentNode) instead of going through a
  // CC-conversion pipeline.

  useBoost: (id: string) => boolean;
  pruneExpiredBoosts: () => void;

  applyDungeonLoot: (
    resources: Array<{ itemId: string; quantity: number }>,
    equipment: EquipmentInstance[],
    xp: number,
    /** v2: amount of Corrupted Data accrued during the run (was credits in v1). */
    corruptedData: number,
    vaultTier?: number,
  ) => void;

  siphonTick: () => SiphonTickResult | null;

  // Build system
  setSkillSlot: (slotIndex: 0 | 1, skillId: SkillId | null) => void;
  activateMarketManip: () => boolean; // returns true if activated

  // Auto-farmer
  setAutoFarmerVault: (vaultId: string | null) => void;
  tickAutoFarmer: () => boolean; // true if a tick fired

  // Avatar
  setAvatarColor: (color: string) => void;

  // Research (Terminal Lab) — legacy v1 path
  upgradeResearch: (nodeId: string) => boolean;
  /**
   * v2: Buy / level up a Skill-Web node (Core / Synergy / Capstone).
   * Costs Clean Code (+ Nano-Chips for higher tiers). Returns true on success.
   */
  upgradeSkillWebNode: (nodeId: string) => boolean;

  // World Bosses
  setBossKillAt: (bossId: string) => void;
}

const STORAGE_KEY = 'cve.game.v1';
const CLICK_CREDITS = 5;
const CLICK_SCRAP_CHANCE = 0.10;
const MARKET_MANIP_DURATION_MS = 60_000;

// Heat-system constants removed in P2.A.5 sunset.

// Legacy v1 ascension cost (one-shot). Phase 7 v2 uses tiered cost — see `ascensionCostV2`.
export const ASCENSION_COST = {
  credits: 100_000,
  hyper_processor_5: 10,
  data_track: 20,
  nano_chip: 50,
} as const;

/** v2 Ascension cost scales 1× / 2× / 4× per tier (T1→T3). Plus 3 duplicates of the same item ID. */
export function ascensionCostV2(targetTier: 1 | 2 | 3): { cleanCode: number; data_track: number; duplicates: number } {
  const mult = Math.pow(2, targetTier - 1); // 1, 2, 4
  return {
    cleanCode: 50_000 * mult,
    data_track: 25 * mult,
    duplicates: 3,
  };
}

/** Roll one of the three Glitch-Perks uniformly at random. */
function rollGlitchPerk(): import('@/domain/types').GlitchPerk {
  const perks: import('@/domain/types').GlitchPerk[] = ['vampiric', 'overloaded', 'hollow'];
  return perks[Math.floor(Math.random() * perks.length)];
}

// Auto-farmer multipliers and per-tier tick intervals.
// Tuning targets (8 h offline session, T1 vault):
//   - 96 → 120 ticks (interval 5 → 4 min)
//   - effective loot multiplier 38.4× → 66× ⇒ ~1 h of active Farm
// Auto stays clearly behind active play (Run / Farm) per-minute, but is
// generous enough that returning after a sleep feels rewarding instead of
// vestigial. Stat-balance-wise it's the AFK "warm welcome", not a grind.
const AUTO_LOOT_MULT = 0.55;
const AUTO_XP_MULT   = 0.45;
export const AUTO_FARM_INTERVALS_MS: Record<number, number> = {
  1:  4 * 60_000,
  2:  6 * 60_000,
  3:  9 * 60_000,
  4: 13 * 60_000,
  5: 18 * 60_000,
};

// Vault prestige income multipliers
const PRESTIGE_MULTIPLIERS: Record<number, number> = {
  0: 1, 1: 1, 2: 2, 3: 5, 4: 15, 5: 50,
};

function prestigeMult(player: Player): number {
  return PRESTIGE_MULTIPLIERS[player.highestVaultTier ?? 0] ?? 1;
}

/** Compound interest from Compounding Interest skill (+1% per threshold credits, max +50%). */
function compoundBonus(player: Player): number {
  const val = activeSkillValue(player, 'compounding_interest');
  if (val === null || val <= 0) return 0;
  return Math.min(0.50, player.credits / val / 100);
}

function buildStarterKit(): EquippedMap {
  // Minimal HK starter: weapon + helmet + armor only. The remaining 5 slots
  // (shield/shoulder/belt/sash/accessory) drop from dungeons + boss kills
  // so the player has a clear progression target from run 1.
  return {
    weapon: { id: randomId('eq'), itemId: 'hk_nail',  upgradeLevel: 0 },
    helmet: { id: randomId('eq'), itemId: 'hk_mask',  upgradeLevel: 0 },
    armor:  { id: randomId('eq'), itemId: 'hk_armor', upgradeLevel: 0 },
  };
}

function seedNewPlayer(player: Player): Player {
  let p = player;
  const seeds: Array<[ItemDef, number]> = [
    [ITEMS.scrap, 10],
    [ITEMS.energy_cell, 3],
  ];
  for (const [item, qty] of seeds) {
    const { inventory } = addItem(p.inventory, item, qty);
    p = withInventory(p, inventory);
  }
  const kit = buildStarterKit();
  p = { ...p, equipment: kit };
  return { ...p, credits: 100 };
}

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      player: null,
      pendingToasts: [],

      createNewPlayer: (name) => {
        set({ player: seedNewPlayer(createPlayer(name)) });
      },

      resetPlayer: () => {
        set({ player: null });
      },

      grantCredits: (amount) => {
        const p = get().player;
        if (!p) return;
        set({ player: addCredits(p, amount) });
      },

      bumpLegendaryPity: (droppedLegendary) => {
        const p = get().player;
        if (!p) return;
        const current = p.legendaryPity ?? 0;
        // P2.C: per-Kill increments instead of per-Run. +0.0001 each kill →
        // ~3000 kills without a legendary to reach the 0.30 cap. Reset on
        // drop. The granular step makes the soft-pity feel responsive
        // ("every kill brings me a tiny bit closer") instead of binary
        // ("did this run roll one or not").
        const next = droppedLegendary ? 0 : Math.min(0.30, current + 0.0001);
        if (next === current) return;
        set({ player: touch({ ...p, legendaryPity: next }) });
      },

      recordEndlessWave: (wave) => {
        const p = get().player;
        if (!p) return;
        const current = p.bestEndlessWave ?? 0;
        if (wave <= current) return;
        set({ player: touch({ ...p, bestEndlessWave: wave }) });
        // Achievement + daily-quest progress for endless milestones.
        get().bumpEvent('endless_wave_reached', wave);
      },

      respecSkillWeb: () => {
        let p = get().player;
        if (!p) return false;
        const respecCount = p.skillWebRespecCount ?? 0;
        // Subsequent respecs: 1000 CD upfront.
        if (respecCount > 0) {
          const r = spendCorruptedData(p, 1000);
          if (!r.ok) return false;
          p = r.player;
        }
        // Walk every node and sum the actual cost of each acquired level —
        // refunds match what the player paid in the first place (no leak).
        let refundCleanCode = 0;
        const refundResources: Partial<Record<ResourceId, number>> = {};
        for (const node of SKILL_WEB_NODES) {
          const lvl = p.skillWebNodes?.[node.id] ?? 0;
          for (let i = 0; i < lvl; i++) {
            const cost = node.cost(i);
            refundCleanCode += cost.cleanCode ?? 0;
            for (const [res, qty] of Object.entries(cost)) {
              if (res === 'cleanCode' || !qty) continue;
              refundResources[res as ResourceId] = (refundResources[res as ResourceId] ?? 0) + qty;
            }
          }
        }
        if (refundCleanCode > 0) p = addCleanCode(p, refundCleanCode);
        for (const [resId, qty] of Object.entries(refundResources)) {
          if (!qty || qty <= 0) continue;
          const def = getItem(resId);
          if (!def) continue;
          const { inventory } = addItem(p.inventory, def, qty);
          p = withInventory(p, inventory);
        }
        // Reset both the node-levels AND the equipped skill-slots. Capstone
        // skills also re-lock, so any equipped slot pointing at a capstone
        // would become invalid. Player re-picks after the respec.
        p = {
          ...p,
          skillWebNodes: {},
          unlockedSkills: [],
          skillSlots: [null, null],
          skillWebRespecCount: respecCount + 1,
        };
        set({ player: touch(p) });
        return true;
      },

      // ── Retention event bus ──────────────────────────────────
      // Single sink: every kill/clear/boss/etc event fires this. Updates
      // BOTH achievement-progress and the daily-quest counter in one pass.
      // Newly unlocked achievements push a toast onto the queue so the UI
      // can surface them without polling.
      bumpEvent: (trigger, amount = 1) => {
        let p = get().player;
        if (!p) return;
        const ach = { ...(p.achievements ?? {}) };
        const queuedToasts: typeof get extends never ? never : { id: string; kind: 'achievement'; achId: string }[] = [];

        for (const def of achievementsByTrigger(trigger)) {
          const prev = ach[def.id] ?? { progress: 0 };
          if (prev.unlockedAt) continue; // already done
          // level_reached uses MAX, everything else accumulates.
          const nextProgress = trigger === 'level_reached'
            ? Math.max(prev.progress, amount)
            : prev.progress + amount;
          const justUnlocked = nextProgress >= def.targetProgress;
          ach[def.id] = {
            ...prev,
            progress: nextProgress,
            unlockedAt: justUnlocked ? Date.now() : prev.unlockedAt,
          };
          if (justUnlocked) {
            queuedToasts.push({
              id: `ach-${def.id}-${Date.now()}`,
              kind: 'achievement',
              achId: def.id,
            });
          }
        }
        p = { ...p, achievements: ach };

        // Daily-quest progress (matching objective).
        const dq = p.dailyQuest;
        if (dq && Date.now() < dq.resetAt && dq.claimedAt === 0) {
          const def = DAILY_QUESTS[dq.id];
          if (def && def.objective === trigger) {
            const nextProgress = trigger === 'level_reached'
              ? Math.max(dq.progress, amount)
              : dq.progress + amount;
            p = { ...p, dailyQuest: { ...dq, progress: Math.min(nextProgress, def.targetProgress) } };
          }
        }

        const updates: Partial<GameState> = { player: touch(p) };
        if (queuedToasts.length > 0) {
          updates.pendingToasts = [...get().pendingToasts, ...queuedToasts];
        }
        set(updates);

        // FTUE auto-advance: certain triggers map 1:1 to a tutorial step.
        // The advance is monotonic (no backwards), so firing on every event
        // is safe and idempotent.
        if (trigger === 'enemy_killed') get().advanceTutorial(2);
        if (trigger === 'all_tier_1_complete') get().advanceTutorial(5);
      },

      claimAchievement: (id) => {
        let p = get().player;
        if (!p) return false;
        const state = p.achievements?.[id];
        if (!state?.unlockedAt || state.claimedAt) return false;
        const def = ACHIEVEMENTS[id];
        if (!def) return false;
        const reward = def.reward;
        if (reward.neonGems && reward.neonGems > 0) {
          p = addNeonGems(p, reward.neonGems);
        }
        let unlockedNewCosmetic = false;
        if (reward.cosmeticId && COSMETICS[reward.cosmeticId]) {
          const owned = { ...(p.unlockedCosmetics ?? {}) };
          if (!owned[reward.cosmeticId]) {
            owned[reward.cosmeticId] = { unlockedAt: Date.now(), equipped: false };
            p = { ...p, unlockedCosmetics: owned };
            unlockedNewCosmetic = true;
          }
        }
        const nextAch = { ...(p.achievements ?? {}) };
        nextAch[id] = { ...state, claimedAt: Date.now() };
        p = { ...p, achievements: nextAch };
        // Commit reward + claim-mark BEFORE bumping the cosmetic_unlocked
        // event — otherwise bumpEvent would read the stale store snapshot
        // (without our +neonGems / +cosmetic mutations) and write it back,
        // clobbering the reward. Self-recursion is bounded: cosmetic_unlocked
        // achievements have no cosmeticId reward, so the recursion terminates
        // after one level.
        set({ player: touch(p) });
        if (unlockedNewCosmetic) {
          get().bumpEvent('cosmetic_unlocked', 1);
        }
        return true;
      },

      claimDailyQuest: () => {
        let p = get().player;
        if (!p) return false;
        const dq = p.dailyQuest;
        if (!dq || dq.claimedAt > 0) return false;
        const def = DAILY_QUESTS[dq.id];
        if (!def || dq.progress < def.targetProgress) return false;
        p = addCorruptedData(p, def.reward.corruptedData);
        p = {
          ...p,
          ascensionFragments: (p.ascensionFragments ?? 0) + def.reward.ascensionFragments,
          dailyQuest: { ...dq, claimedAt: Date.now() },
        };
        set({ player: touch(p) });
        return true;
      },

      refreshDailyQuest: () => {
        const p = get().player;
        if (!p) return;
        const now = Date.now();
        const dq = p.dailyQuest;
        // Already assigned for the current day-window → leave it alone.
        if (dq && dq.resetAt > now) return;
        const def = pickDailyQuest(p.id, now);
        const fresh: Player['dailyQuest'] = {
          id: def.id,
          progress: 0,
          claimedAt: 0,
          resetAt: nextResetAt(now),
        };
        set({ player: touch({ ...p, dailyQuest: fresh }) });
      },

      dismissToast: (id) => {
        set({ pendingToasts: get().pendingToasts.filter((t) => t.id !== id) });
      },

      setCosmeticEquipped: (cosmeticId, equipped) => {
        let p = get().player;
        if (!p) return false;
        const owned = p.unlockedCosmetics ?? {};
        if (!owned[cosmeticId]) return false;
        const def = COSMETICS[cosmeticId];
        if (!def) return false;
        // Unequip everything in the same category first (mutually exclusive).
        const nextOwned: typeof owned = {};
        for (const [id, state] of Object.entries(owned)) {
          const otherDef = COSMETICS[id];
          const sameCat = otherDef && otherDef.category === def.category;
          nextOwned[id] = { ...state, equipped: sameCat ? false : state.equipped };
        }
        nextOwned[cosmeticId] = { ...nextOwned[cosmeticId], equipped };
        // Sync avatarColor for the equipped avatar_color cosmetic so the
        // existing PlayerAvatar component (reads player.avatarColor) keeps
        // working unchanged.
        let avatarColor = p.avatarColor;
        if (def.category === 'avatar_color') {
          if (equipped) avatarColor = def.value;
          else if (!equipped && owned[cosmeticId]?.equipped) avatarColor = 'blue';
        }
        p = { ...p, unlockedCosmetics: nextOwned, avatarColor };
        set({ player: touch(p) });
        return true;
      },

      buyCosmetic: (cosmeticId) => {
        let p = get().player;
        if (!p) return false;
        const def = COSMETICS[cosmeticId];
        if (!def || def.neonGemCost <= 0) return false;
        if ((p.unlockedCosmetics ?? {})[cosmeticId]) return false; // already owned
        const r = spendNeonGems(p, def.neonGemCost);
        if (!r.ok) return false;
        p = r.player;
        const owned = { ...(p.unlockedCosmetics ?? {}) };
        owned[cosmeticId] = { unlockedAt: Date.now(), equipped: false };
        p = { ...p, unlockedCosmetics: owned };
        set({ player: touch(p) });
        get().bumpEvent('cosmetic_unlocked', 1);
        return true;
      },

      advanceTutorial: (toStep) => {
        const p = get().player;
        if (!p) return;
        const cur = p.tutorialStep ?? 0;
        if (cur >= toStep) return;        // monotonic: only forward
        if (cur >= TUTORIAL_DONE) return; // already done
        set({ player: touch({ ...p, tutorialStep: toStep }) });
      },

      skipTutorial: () => {
        const p = get().player;
        if (!p) return;
        set({ player: touch({ ...p, tutorialStep: TUTORIAL_DONE }) });
      },

      rollbackToBackup: () => {
        const p = get().player;
        if (!p?._legacyBackup) return false;
        const TTL_MS = 30 * 24 * 60 * 60 * 1000;
        if (Date.now() - p._legacyBackup.createdAt > TTL_MS) return false;
        // Restore the snapshot. The backup itself is consumed (won't be there
        // after restore) — but the same restore can be re-run if the next
        // migration writes a fresh backup. createdAt stays so we know when.
        const restored = { ...p._legacyBackup.snapshot };
        set({ player: touch(restored) });
        return true;
      },

      buyAugmentNode: (branch) => {
        const p = get().player;
        if (!p) return false;
        const augments = p.augments ?? emptyAugments();
        const owned = augments[branch] ?? 0;
        const branchDef = AUGMENT_BRANCHES[branch];
        if (!branchDef || owned >= branchDef.nodes.length) return false;
        const nextPos = owned + 1;
        const cost = augmentCost(nextPos);
        const cd = p.corruptedData ?? 0;
        if (cd < cost) return false;
        const r = spendCorruptedData(p, cost);
        if (!r.ok) return false;
        const next: Player = {
          ...r.player,
          augments: { ...augments, [branch]: nextPos },
        };
        set({ player: touch(next) });
        return true;
      },

      setAspect: (slot, aspectId) => {
        const p = get().player;
        if (!p) return false;
        const aspects = p.aspects ?? defaultAspectsState();
        const tier = p.highestVaultTier ?? 0;
        // Null only valid on secondary slot.
        if (aspectId === null && slot === 'primary') return false;
        if (aspectId !== null) {
          // Unlock-check is TIER-BASED (the cached unlocked-array is a stale
          // denormalization). Player can slot any aspect whose tier-gate is met.
          if (tier < ASPECTS[aspectId].unlock.vaultTier) return false;
          // Prevent same aspect in both slots.
          const otherSlot: AspectSlot = slot === 'primary' ? 'secondary' : 'primary';
          if (aspects[otherSlot] === aspectId) return false;
          // Secondary slot requires Tau-Vault clear (highestVaultTier >= 3).
          if (slot === 'secondary' && tier < 3) return false;
        }
        const nextAspects = { ...aspects, [slot]: aspectId };
        // Always sync the persisted unlocked-cache to reflect current tier.
        const unlockedSet = new Set(aspects.unlocked);
        for (const id of Object.keys(ASPECTS) as AspectId[]) {
          if (tier >= ASPECTS[id].unlock.vaultTier) unlockedSet.add(id);
        }
        nextAspects.unlocked = Array.from(unlockedSet);
        // First-time slot of an aspect: grant L1 + add active-skill to pool.
        let nextUnlockedActiveSkills = p.unlockedActiveSkills ?? [];
        let nextLevels = nextAspects.levels;
        if (aspectId && (nextLevels[aspectId] ?? 0) === 0) {
          nextLevels = { ...nextLevels, [aspectId]: 1 };
          const def = ASPECTS[aspectId];
          if (def && !nextUnlockedActiveSkills.includes(def.activeSkillId)) {
            nextUnlockedActiveSkills = [...nextUnlockedActiveSkills, def.activeSkillId];
          }
        }
        nextAspects.levels = nextLevels;
        set({ player: touch({
          ...p, aspects: nextAspects, unlockedActiveSkills: nextUnlockedActiveSkills,
        }) });
        return true;
      },

      investAspectLevel: (aspectId) => {
        let p = get().player;
        if (!p) return false;
        const aspects = p.aspects ?? defaultAspectsState();
        if (!aspects.unlocked.includes(aspectId)) return false;
        const cur = aspects.levels[aspectId] ?? 0;
        if (cur >= 5) return false;
        // L1 is granted on slot (handled in setAspect); investment starts at L2.
        if (cur === 0) return false;
        const cost = aspectLevelUpCost(cur);
        if (cost <= 0) return false;
        const r = spendCorruptedData(p, cost);
        if (!r.ok) return false;
        p = r.player;
        const nextLevels = { ...aspects.levels, [aspectId]: cur + 1 };
        set({ player: touch({ ...p, aspects: { ...aspects, levels: nextLevels } }) });
        return true;
      },

      setActiveSkill: (skillId) => {
        const p = get().player;
        if (!p) return false;
        if (skillId !== null) {
          // Pool = explicit `unlockedActiveSkills` ∪ all-unlocked-aspects'
          // active-skills. Derived form ensures migration gaps don't lock
          // the player out of slotting a valid skill.
          const poolSet = new Set(p.unlockedActiveSkills ?? []);
          for (const aId of (p.aspects?.unlocked ?? [])) {
            poolSet.add(ASPECTS[aId].activeSkillId);
          }
          if (!poolSet.has(skillId)) return false;
          // Sync the persisted list so subsequent reads have it cached.
          const merged = Array.from(poolSet);
          set({ player: touch({ ...p, activeSkillId: skillId, unlockedActiveSkills: merged }) });
          return true;
        }
        set({ player: touch({ ...p, activeSkillId: null }) });
        return true;
      },

      tryBuy: (amount) => {
        const p = get().player;
        if (!p) return false;
        const result = spendCredits(p, amount);
        if (!result.ok) return false;
        set({ player: result.player });
        return true;
      },

      grantXp: (amount) => {
        const p = get().player;
        if (!p) return;
        set({ player: addXp(p, amount) });
      },

      giveItem: (item, quantity = 1) => {
        const p = get().player;
        if (!p) return 0;
        const { inventory, added } = addItem(p.inventory, item, quantity);
        if (added > 0) set({ player: touch(withInventory(p, inventory)) });
        return added;
      },

      takeItem: (itemId, quantity = 1) => {
        const p = get().player;
        if (!p) return false;
        const { inventory, ok } = removeItem(p.inventory, itemId, quantity);
        if (!ok) return false;
        set({ player: touch(withInventory(p, inventory)) });
        return true;
      },

      equipInstance: (instanceId) => {
        const p = get().player;
        if (!p) return false;
        const result = doEquip(p, instanceId);
        if (!result.ok) return false;
        set({ player: result.player });
        return true;
      },

      unequipSlot: (slot) => {
        const p = get().player;
        if (!p) return false;
        const result = unequipSlot(p, slot);
        if (!result.ok) return false;
        set({ player: result.player });
        return true;
      },

      upgradeInstance: (instanceId) => {
        const p = get().player;
        if (!p) return { ok: false };
        const inst = findInstance(p, instanceId);
        if (!inst) return { ok: false };
        const def = getItem(inst.itemId);
        if (!def) return { ok: false };
        if (inst.upgradeLevel >= maxLevel(def)) return { ok: false };

        const cost = upgradeCost(def, inst.upgradeLevel);
        if (!canAfford(p, cost)) return { ok: false };

        let next = spendCost(p, cost);
        const upgraded: EquipmentInstance = { ...inst, upgradeLevel: inst.upgradeLevel + 1 };
        next = replaceInstance(next, upgraded);
        set({ player: touch(next) });
        // Fires achievement+daily-quest only when the upgrade lands at maxLevel.
        if (upgraded.upgradeLevel === maxLevel(def)) {
          get().bumpEvent('item_upgraded_to_max', 1);
        }
        // FTUE: ANY upgrade satisfies step 4 (player learned the gesture).
        get().advanceTutorial(4);
        return { ok: true, newLevel: upgraded.upgradeLevel };
      },

      reforgeBonusStats: (instanceId) => {
        const p = get().player;
        if (!p) return { ok: false };
        // Locate the instance — can be equipped OR in stash. Reforge works
        // on either; the player doesn't have to unequip first.
        const equippedSlot = (Object.keys(p.equipment) as Array<keyof typeof p.equipment>)
          .find((slot) => p.equipment[slot]?.id === instanceId);
        const stashIdx = p.equipmentStash.findIndex((i) => i.id === instanceId);
        const inst = equippedSlot
          ? p.equipment[equippedSlot]
          : stashIdx >= 0 ? p.equipmentStash[stashIdx] : null;
        if (!inst) return { ok: false };
        const def = getItem(inst.itemId);
        if (!def) return { ok: false };

        // Cost: 5× nano_chip + 1× data_track. Atomic — check both, then
        // deduct both, so a partial-failure never bills the player.
        if (!hasItem(p.inventory, 'nano_chip', 5)) return { ok: false };
        if (!hasItem(p.inventory, 'data_track', 1)) return { ok: false };

        // Re-roll affixes using the item's rarity table. We always emit at
        // least the rarity-mandated count so a "common" item that previously
        // had zero affixes can stay at zero — matching item-drop semantics.
        const newStats = rollBonusStats(def.rarity);

        // Build the next player state in one shot so a single set() flush
        // covers inventory deduction + bonusStats replacement.
        let nextInv = removeItem(p.inventory, 'nano_chip', 5).inventory;
        nextInv = removeItem(nextInv, 'data_track', 1).inventory;
        const nextInstance = { ...inst, bonusStats: newStats.length > 0 ? newStats : undefined };
        const nextPlayer = { ...p, inventory: nextInv };
        if (equippedSlot) {
          nextPlayer.equipment = { ...p.equipment, [equippedSlot]: nextInstance };
        } else if (stashIdx >= 0) {
          const stash = [...p.equipmentStash];
          stash[stashIdx] = nextInstance;
          nextPlayer.equipmentStash = stash;
        }
        set({ player: touch(nextPlayer) });
        return { ok: true, newStats };
      },

      salvageInstance: (instanceId) => {
        const p = get().player;
        if (!p) return false;
        const inst = p.equipmentStash.find((i) => i.id === instanceId);
        if (!inst) return false;
        const def = getItem(inst.itemId);
        // Salvage Expert skill increases return rate (0.60→1.00, default 0.50).
        // P2.A Engineer E3: salvage_refund_pct stacks on top of that.
        const baseRate = activeSkillValue(p, 'salvage_expert') ?? 0.50;
        const augRefundMult = 1 + totalAugmentValue(p.augments, 'salvage_refund_pct');
        const rate = baseRate * augRefundMult;
        const refund = Math.floor((def?.baseValue ?? 10) * rate);
        const scrapBack = Math.floor(inst.upgradeLevel * 2 * augRefundMult);
        let next = removeFromStash(p, instanceId);
        next = addCredits(next, refund);
        if (scrapBack > 0) {
          const { inventory } = addItem(next.inventory, ITEMS.scrap, scrapBack);
          next = withInventory(next, inventory);
        }
        set({ player: touch(next) });
        return true;
      },

      salvageStashByRarity: (rarity) => {
        const p = get().player;
        if (!p) return 0;
        const targets = p.equipmentStash.filter((inst) => {
          if (rarity === 'all') return true;
          const def = getItem(inst.itemId);
          return def?.rarity === rarity;
        });
        if (targets.length === 0) return 0;

        const rate = activeSkillValue(p, 'salvage_expert') ?? 0.50;
        let totalCredits = 0;
        let totalScrap = 0;
        // Fragments are awarded per L10 item salvaged — turns "useless duplicate
        // of a maxed item" into a clear progression resource. Fragments are
        // universal across item types: a salvaged L10 sword can ascend a helmet.
        let fragmentsGained = 0;
        const targetIds = new Set(targets.map((t) => t.id));
        for (const inst of targets) {
          const def = getItem(inst.itemId);
          totalCredits += Math.floor((def?.baseValue ?? 10) * rate);
          totalScrap += inst.upgradeLevel * 2;
          if (def && inst.upgradeLevel >= maxLevel(def)) fragmentsGained += 1;
        }

        let next: Player = {
          ...p,
          equipmentStash: p.equipmentStash.filter((i) => !targetIds.has(i.id)),
          ascensionFragments: (p.ascensionFragments ?? 0) + fragmentsGained,
          updatedAt: Date.now(),
        };
        next = addCredits(next, totalCredits);
        if (totalScrap > 0) {
          const { inventory } = addItem(next.inventory, ITEMS.scrap, totalScrap);
          next = withInventory(next, inventory);
        }
        set({ player: touch(next) });
        return targets.length;
      },

      ascendInstance: (instanceId) => {
        const p = get().player;
        if (!p) return false;
        const inst = findInstance(p, instanceId);
        if (!inst) return false;
        const def = getItem(inst.itemId);
        if (!def) return false;
        if (inst.upgradeLevel < maxLevel(def)) return false; // must be max level
        const currentTier = inst.ascensionTier ?? (inst.ascended ? 1 : 0);
        if (currentTier >= 3) return false;                   // Mythic is the cap
        const targetTier = (currentTier + 1) as 1 | 2 | 3;
        const cost = ascensionCostV2(targetTier);

        // v3 progression: ascending no longer demands 3 IDENTICAL duplicates of
        // the exact item — instead it costs 3 universal Ascension Fragments
        // earned by salvaging any L10 item. Removes the "farm 4 copies of the
        // same drop" wall that made endgame ascend feel impossible.
        const fragments = p.ascensionFragments ?? 0;
        if (fragments < cost.duplicates) return false;

        // Pay Clean Code + Data-Tracks (unchanged from v2)
        if ((p.cleanCode ?? 0) < cost.cleanCode) return false;
        const resCost: Partial<Record<ResourceId, number>> = { data_track: cost.data_track };
        if (!canAfford(p, resCost)) return false;

        let next: Player = p;
        const r = spendCleanCode(next, cost.cleanCode);
        if (!r.ok) return false;
        next = spendCost(r.player, resCost);
        next = { ...next, ascensionFragments: fragments - cost.duplicates };

        // Roll a fresh Glitch-Perk for this ascension
        const perk = rollGlitchPerk();
        const newPerks = [...(inst.glitchPerks ?? []), perk];
        const ascended: import('@/domain/types').EquipmentInstance = {
          ...inst,
          ascensionTier: targetTier,
          glitchPerks: newPerks,
          ascended: true,                       // keep legacy flag in sync
          upgradeLevel: 1,                       // resets to Lv 1 per spec ("falls auf Level 1 zurück")
        };
        next = replaceInstance(next, ascended);
        set({ player: touch(next) });
        get().bumpEvent('item_ascended', 1);
        return true;
      },

      buyMiner: (id) => {
        const p = get().player;
        if (!p) return false;
        const def: AutoMinerDef | undefined = MINERS[id];
        if (!def) return false;
        if (p.level < def.unlockLevel) return false;
        const owned = ownedCount(p, id);
        const cost = minerCost(def, owned);
        if (!canAfford(p, cost)) return false;
        let next = spendCost(p, cost);
        next = { ...next, autoMiners: { ...next.autoMiners, [id]: owned + 1 } };
        set({ player: touch(next) });
        return true;
      },

      tickMiners: () => {
        const p = get().player;
        if (!p) return {};
        const now = Date.now();
        const delta = accrueOfflineYield(p.autoMiners, p.lastTickAt, now);
        if (Object.keys(delta).length === 0) {
          set({ player: { ...p, lastTickAt: now } });
          return {};
        }

        // Economy multipliers
        const prestige = prestigeMult(p);
        const compound = 1 + compoundBonus(p);
        const marketActive = p.marketManipEndsAt > now;
        const marketMult = marketActive
          ? (activeSkillValue(p, 'market_manipulation') ?? 1.5)
          : 1;
        const totalMult = prestige * compound * marketMult;

        let next = p;
        const scaledDelta: Partial<Record<ResourceId | 'credits', number>> = {};
        for (const [k, v] of Object.entries(delta) as Array<[ResourceId | 'credits', number]>) {
          if (!v) continue;
          let scaled = Math.floor(v * totalMult);

          // Scrap Baron 4-piece: 5% chance for nano_chip instead during Market Manip
          if (marketActive && k !== 'credits' && hasSetBonus(p.equipment, 'market_premium')
              && Math.random() < 0.05) {
            const chipDef = ITEMS.nano_chip;
            const { inventory } = addItem(next.inventory, chipDef, 1);
            next = withInventory(next, inventory);
            scaled = 0;
          }

          if (scaled <= 0) continue;
          scaledDelta[k] = scaled;

          if (k === 'credits') {
            next = addCredits(next, scaled);
          } else {
            const itemDef = getItem(k);
            if (!itemDef) continue;
            const { inventory } = addItem(next.inventory, itemDef, scaled);
            next = withInventory(next, inventory);
          }
        }
        next = { ...next, lastTickAt: now };
        set({ player: touch(next) });
        return scaledDelta;
      },

      useBoost: (id) => {
        const p = get().player;
        if (!p) return false;
        const def = getBoost(id);
        if (!def) return false;
        if (!canAfford(p, def.cost)) return false;
        let next = spendCost(p, def.cost);
        // P2.A Engineer E1: boost_duration_pct extends the duration. Apply
        // by patching a copy of the def with the augmented duration value.
        const augDurMult = 1 + totalAugmentValue(p.augments, 'boost_duration_pct');
        const defForActivate = augDurMult !== 1
          ? { ...def, durationSec: Math.round(def.durationSec * augDurMult) }
          : def;
        const activated = activateBoost(next, defForActivate);
        next = activated.player;
        set({ player: touch(next) });
        return true;
      },

      pruneExpiredBoosts: () => {
        const p = get().player;
        if (!p) return;
        const active = filterActive(p.boosts);
        if (active.length !== p.boosts.length) {
          set({ player: { ...p, boosts: active } });
        }
      },

      applyDungeonLoot: (resources, equipment, xp, corruptedData, vaultTier) => {
        let p = get().player;
        if (!p) return;
        const prevLevel = p.level;
        const augXpMult = 1 + totalAugmentValue(p.augments, 'xp_pct');
        const xpMult = (1 + researchBonus(p.research ?? {}, 'xp_pct') / 100) * augXpMult;
        p = addCorruptedData(p, corruptedData);
        p = addXp(p, Math.round(xp * xpMult));
        for (const entry of resources) {
          const def = getItem(entry.itemId);
          if (!def) continue;
          const { inventory } = addItem(p.inventory, def, entry.quantity);
          p = withInventory(p, inventory);
        }
        if (equipment.length > 0) {
          // Auto-salvage duplicates of items the player already owns at max
          // ascension (Tier 3 = Mythic). Without this, drops of fully-ascended
          // HK pieces would pile up in the stash with no use. Each auto-salvaged
          // drop converts to +1 Ascension-Fragment so the work isn't wasted.
          const ownedTier3Ids = new Set<string>();
          for (const slot of Object.values(p.equipment ?? {})) {
            if (slot && (slot.ascensionTier ?? 0) >= 3) ownedTier3Ids.add(slot.itemId);
          }
          for (const inst of p.equipmentStash) {
            if ((inst.ascensionTier ?? 0) >= 3) ownedTier3Ids.add(inst.itemId);
          }
          const toStash: typeof equipment = [];
          let autoFragments = 0;
          for (const inst of equipment) {
            if (ownedTier3Ids.has(inst.itemId)) autoFragments += 1;
            else toStash.push(inst);
          }
          if (toStash.length > 0) p = { ...p, equipmentStash: [...p.equipmentStash, ...toStash] };
          if (autoFragments > 0) p = { ...p, ascensionFragments: (p.ascensionFragments ?? 0) + autoFragments };
        }
        // Track highest vault tier + unlock skills
        const prevHighestTier = p.highestVaultTier ?? 0;
        if (vaultTier && vaultTier > prevHighestTier) {
          const newHighest = vaultTier;
          const newSkills = skillsUnlockedByTier(newHighest).filter(
            (id) => !(p!.unlockedSkills ?? []).includes(id),
          );
          p = {
            ...p,
            highestVaultTier: newHighest,
            unlockedSkills: [...(p.unlockedSkills ?? []), ...newSkills],
          };
        }
        set({ player: touch(p) });

        // ── Event triggers — fire AFTER state is set so bumpEvent reads the
        //    fresh player from get(). cosmetic_unlocked etc. self-recurse safely.
        if (vaultTier) get().bumpEvent('vault_cleared', 1);
        if (corruptedData > 0) get().bumpEvent('cd_earned_total', corruptedData);
        const legendaryDrops = equipment.filter((e) => {
          const def = getItem(e.itemId);
          return def?.rarity === 'legendary' || def?.rarity === 'mythic';
        }).length;
        if (legendaryDrops > 0) {
          get().bumpEvent('legendary_dropped', legendaryDrops);
          get().bumpEvent('first_legendary', 1);
        }
        const newLevel = (get().player?.level ?? prevLevel);
        if (newLevel > prevLevel) get().bumpEvent('level_reached', newLevel);
        const newHighestTier = get().player?.highestVaultTier ?? prevHighestTier;
        if (newHighestTier >= 1 && prevHighestTier < 1) {
          get().bumpEvent('all_tier_1_complete', 1);
        }
        // FTUE: first equipment drop in a run → step 3 (claim hint).
        if (equipment.length > 0) get().advanceTutorial(3);
      },

      siphonTick: () => {
        const p = get().player;
        if (!p) return null;
        const prestige = prestigeMult(p);
        const compound = 1 + compoundBonus(p);
        const clickResearch = 1 + researchBonus(p.research ?? {}, 'click_credits') / 100;
        const totalMult = prestige * compound * clickResearch;
        const earned = Math.max(1, Math.floor(CLICK_CREDITS * totalMult));

        let next = addCredits(p, earned);
        let dropped: ItemDef | null = null;
        if (Math.random() < CLICK_SCRAP_CHANCE) {
          dropped = ITEMS.scrap;
          const { inventory } = addItem(next.inventory, dropped, 1);
          next = withInventory(next, inventory);
        }

        // Scrap Baron 2-piece: 20% chance to tick all miners on click
        if (hasSetBonus(p.equipment, 'terminal_tick') && Math.random() < 0.20) {
          const now = Date.now();
          const delta = accrueOfflineYield(next.autoMiners, now - 1000, now);
          for (const [k, v] of Object.entries(delta) as Array<[ResourceId | 'credits', number]>) {
            if (!v) continue;
            if (k === 'credits') {
              next = addCredits(next, Math.floor(v * prestige));
            } else {
              const itemDef = getItem(k);
              if (itemDef) {
                const { inventory } = addItem(next.inventory, itemDef, v);
                next = withInventory(next, inventory);
              }
            }
          }
        }

        set({ player: touch(next) });
        return { credits: earned, itemDropped: dropped };
      },

      setSkillSlot: (slotIndex, skillId) => {
        const p = get().player;
        if (!p) return;
        if (skillId && !p.unlockedSkills.includes(skillId)) return;
        const newSlots: [SkillId | null, SkillId | null] = [...p.skillSlots] as [SkillId | null, SkillId | null];
        // Remove from other slot if already assigned
        if (skillId && newSlots[1 - slotIndex] === skillId) {
          newSlots[1 - slotIndex] = null;
        }
        newSlots[slotIndex] = skillId;
        set({ player: touch({ ...p, skillSlots: newSlots }) });
      },

      activateMarketManip: () => {
        const p = get().player;
        if (!p) return false;
        if (!hasSkill(p, 'market_manipulation')) return false;
        set({ player: touch({ ...p, marketManipEndsAt: Date.now() + MARKET_MANIP_DURATION_MS }) });
        return true;
      },

      setAvatarColor: (color) => {
        const p = get().player;
        if (!p) return;
        set({ player: touch({ ...p, avatarColor: color }) });
      },

      setBossKillAt: (bossId: string) => {
        const p = get().player;
        if (!p) return;
        set({ player: touch({ ...p, lastBossKillAt: { ...(p.lastBossKillAt ?? {}), [bossId]: Date.now() } }) });
      },

      upgradeResearch: (nodeId) => {
        const p = get().player;
        if (!p) return false;
        const node = RESEARCH_NODES.find((n) => n.id === nodeId);
        if (!node) return false;
        const current = (p.research ?? {})[nodeId] ?? 0;
        if (current >= node.maxLevel) return false;
        const cost = node.costPerLevel(current);
        if (!canAfford(p, cost)) return false;
        const next = spendCost({ ...p, research: { ...(p.research ?? {}), [nodeId]: current + 1 } }, cost);
        set({ player: touch(next) });
        return true;
      },

      upgradeSkillWebNode: (nodeId) => {
        const p = get().player;
        if (!p) return false;
        const node = getSkillWebNode(nodeId);
        if (!node) return false;
        if (!isNodeAvailable(p, node)) return false;
        const current = nodeLevel(p, nodeId);
        if (current >= node.maxLevel) return false;
        const cost = node.cost(current);
        // Pay Clean Code (primary) + any inventory-resource costs (nano_chip, data_track, etc.)
        if ((cost.cleanCode ?? 0) > (p.cleanCode ?? 0)) return false;
        const resCost: Partial<Record<ResourceId, number>> = {};
        for (const k of ['nano_chip', 'data_track', 'hyper_processor_5', 'scrap', 'energy_cell'] as ResourceId[]) {
          const v = (cost as Record<string, number | undefined>)[k];
          if (v) resCost[k] = v;
        }
        if (Object.keys(resCost).length > 0 && !canAfford(p, resCost)) return false;
        let next: Player = p;
        if (cost.cleanCode) {
          const r = spendCleanCode(next, cost.cleanCode);
          if (!r.ok) return false;
          next = r.player;
        }
        if (Object.keys(resCost).length > 0) next = spendCost(next, resCost);
        next = {
          ...next,
          skillWebNodes: { ...(next.skillWebNodes ?? {}), [nodeId]: current + 1 },
        };
        // Capstone unlock: also add the SkillId to unlockedSkills (legacy slot system reads from there).
        if (node.ring === 'capstone' && node.capstoneSkillId && current === 0) {
          const already = (next.unlockedSkills ?? []).includes(node.capstoneSkillId);
          if (!already) {
            next = { ...next, unlockedSkills: [...(next.unlockedSkills ?? []), node.capstoneSkillId] };
          }
        }
        set({ player: touch(next) });
        return true;
      },

      setAutoFarmerVault: (vaultId) => {
        const p = get().player;
        if (!p) return;
        set({ player: touch({ ...p, autoFarmerVaultId: vaultId }) });
      },

      tickAutoFarmer: () => {
        const p = get().player;
        if (!p || !p.autoFarmerVaultId) return false;
        const vault = getVault(p.autoFarmerVaultId);
        if (!vault) return false;
        if ((p.highestVaultTier ?? 0) < vault.tier) return false;

        const interval = AUTO_FARM_INTERVALS_MS[vault.tier] ?? 60_000;
        const now = Date.now();
        const elapsed = now - (p.lastAutoFarmAt ?? 0);
        if (elapsed < interval) return false;

        // Offline catch-up: apply all missed ticks (cap at 8 hours)
        const MAX_OFFLINE_MS = 8 * 60 * 60_000;
        const tickCount = Math.floor(Math.min(elapsed, MAX_OFFLINE_MS) / interval);
        if (tickCount === 0) return false;

        const overshoot = Math.max(0, p.level - vault.minLevel);
        const hpMult = calcScaledHpMult(vault, p.level);

        // Accumulate XP + Corrupted Data across all ticks
        const wave = vault.waves[0];
        let xpPerTick = 0, cdPerTick = 0;
        for (const enemyId of wave.enemies) {
          const enemy = getEnemy(enemyId);
          if (enemy) {
            xpPerTick += Math.round(enemy.xpReward * hpMult * AUTO_XP_MULT);
            cdPerTick += Math.round(enemy.corruptedDataReward * hpMult * AUTO_XP_MULT);
          }
        }

        // Roll loot once per tick, accumulate resources + equipment
        const resourceTotals: Record<string, number> = {};
        const allEquipment: ReturnType<typeof rollTierLoot>['equipment'] = [];
        for (let i = 0; i < tickCount; i++) {
          const drops = rollTierLoot(vault.dropTier, AUTO_LOOT_MULT, false, 0, 0, 0, overshoot);
          for (const r of drops.resources) {
            resourceTotals[r.itemId] = (resourceTotals[r.itemId] ?? 0) + r.quantity;
          }
          allEquipment.push(...drops.equipment);
        }

        let next = p;
        next = addCorruptedData(next, cdPerTick * tickCount);
        next = addXp(next, xpPerTick * tickCount);
        for (const [itemId, qty] of Object.entries(resourceTotals)) {
          const def = getItem(itemId);
          if (def) {
            const { inventory } = addItem(next.inventory, def, qty);
            next = withInventory(next, inventory);
          }
        }
        if (allEquipment.length > 0) {
          next = { ...next, equipmentStash: [...next.equipmentStash, ...allEquipment] };
        }
        next = { ...next, lastAutoFarmAt: now };
        set({ player: touch(next) });
        return true;
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      version: 13,
      migrate: (persisted, version) => {
        const state = persisted as { player?: Player & { equipment?: EquippedMap | Record<string, string> } };
        if (!state.player) return state;
        const p = state.player as Player & { equipment?: unknown };

        // ── Migration backup ─────────────────────────────────────
        // BEFORE any mutation: snapshot the current player so a botched
        // migration can be undone via gameStore.rollbackToBackup(). One slot
        // only (FIFO, the latest pre-migration state wins). 30-day TTL
        // prevents the localStorage payload from accumulating over a year.
        const LEGACY_BACKUP_TTL_MS = 30 * 24 * 60 * 60 * 1000;
        if (version < 10) {
          // Drop the snapshot's own _legacyBackup before storing to avoid
          // recursive nesting (snapshot-of-snapshot-of-snapshot).
          const snapshotClone = JSON.parse(JSON.stringify(p)) as Player;
          delete (snapshotClone as { _legacyBackup?: unknown })._legacyBackup;
          p._legacyBackup = {
            fromVersion: version,
            createdAt: Date.now(),
            snapshot: snapshotClone,
          };
        }
        // TTL cleanup on every migration
        if (p._legacyBackup && Date.now() - p._legacyBackup.createdAt > LEGACY_BACKUP_TTL_MS) {
          delete p._legacyBackup;
        }

        if (version < 2) {
          if (!p.equipment) p.equipment = {};
          if (p.currentHp == null) p.currentHp = 50;
        }
        if (version < 3) {
          const old = (p.equipment ?? {}) as Record<string, unknown>;
          const next: EquippedMap = {};
          for (const slot of Object.keys(old)) {
            const val = old[slot];
            if (typeof val === 'string' && val) {
              next[slot as EquipmentSlot] = { id: randomId('eq'), itemId: val, upgradeLevel: 0 };
            } else if (val && typeof val === 'object' && 'itemId' in val) {
              next[slot as EquipmentSlot] = val as EquipmentInstance;
            }
          }
          p.equipment = next;
          const pAny = p as unknown as Record<string, unknown>;
          p.equipmentStash = (pAny.equipmentStash as EquipmentInstance[] | undefined) ?? [];
          p.autoMiners = (pAny.autoMiners as Record<string, number> | undefined) ?? {};
          p.boosts = (pAny.boosts as Player['boosts'] | undefined) ?? [];
          p.lastTickAt = (pAny.lastTickAt as number | undefined) ?? Date.now();
        }
        if (version < 4) {
          const kit = buildStarterKit();
          const eq = (p.equipment ?? {}) as EquippedMap;
          const slots: EquipmentSlot[] = ['weapon', 'helmet', 'armor', 'accessory'];
          for (const s of slots) if (!eq[s]) eq[s] = kit[s];
          p.equipment = eq;
          if (p.credits < 50) p.credits = 50;
        }
        if (version < 5) {
          // Initialise new build-system fields
          const pAny = p as unknown as Record<string, unknown>;
          if (pAny.highestVaultTier == null) pAny.highestVaultTier = 0;
          if (!Array.isArray(pAny.skillSlots)) pAny.skillSlots = [null, null];
          if (!Array.isArray(pAny.unlockedSkills)) pAny.unlockedSkills = [];
          if (pAny.marketManipEndsAt == null) pAny.marketManipEndsAt = 0;
        }
        if (version < 6) {
          const pAny = p as unknown as Record<string, unknown>;
          if (pAny.autoFarmerVaultId === undefined) pAny.autoFarmerVaultId = null;
          if (pAny.lastAutoFarmAt == null) pAny.lastAutoFarmAt = 0;
        }
        if (version < 7) {
          // Retroactively grant any skills the player should have earned based on
          // their highestVaultTier (new skills added after they cleared those vaults).
          const tier = (p.highestVaultTier ?? 0) as number;
          const due = skillsUnlockedByTier(tier);
          const current = (p.unlockedSkills ?? []) as string[];
          const missing = due.filter((id) => !current.includes(id));
          if (missing.length > 0) {
            p.unlockedSkills = [...current, ...missing] as typeof p.unlockedSkills;
          }
        }
        if (version < 8) {
          const pAny = p as unknown as Record<string, unknown>;
          if (!pAny.avatarColor) pAny.avatarColor = 'blue';
          if (!pAny.research) pAny.research = {};
          if (!pAny.lastBossKillAt) pAny.lastBossKillAt = {};
        }
        if (version < 9) {
          // Hybrid v1 → v2 migration: items + level + stash kept, currencies + research +
          // skill-slots wiped; legacy ascended:true items get ascensionTier=1; free
          // Scrap-Parser seeded so CD → Clean-Code pipeline is playable immediately.
          state.player = migrateToV2(p as Player);

          // Always-on seed: even if migrateToV2 was a no-op (gameVersion already ≥ 2 from
          // a partial Phase-1 deploy), make sure the free Scrap-Parser is available.
          const sp = state.player as Player;
          if (!sp.decrypters?.scrap_parser || sp.decrypters.scrap_parser.level < 1) {
            sp.decrypters = {
              ...(sp.decrypters ?? {}),
              scrap_parser: { level: 1, buffer: 0, storageMultiplier: 1 },
            };
          }
        }
        if (version < 10) {
          // P1 Retention-Foundation: seed the new fields on legacy saves so
          // accessing `player.unlockedCosmetics`, `achievements`, `dailyQuest`,
          // `tutorialStep` returns sensible defaults instead of undefined.
          const sp = state.player as Player;
          if (!sp.unlockedCosmetics) sp.unlockedCosmetics = {};
          if (!sp.achievements) sp.achievements = {};
          if (sp.dailyQuest === undefined) sp.dailyQuest = null;
          if (sp.tutorialStep == null) sp.tutorialStep = 0;
          state.player = sp;
        }
        if (version < 11) {
          // P2.A Augment-Tree: initialise empty branch counters. CD-investments
          // are completely separate from the previous Skill-Web sink, no refund
          // needed at this stage (sunset comes in P2.A.5).
          const sp = state.player as Player;
          if (!sp.augments) {
            sp.augments = { survivor: 0, aggressor: 0, hunter: 0, engineer: 0, mystic: 0 };
          }
          state.player = sp;
        }
        if (version < 13) {
          // P2.B Aspekt-System: ensure every legacy player has a valid
          // primary slot + Bulwark unlocked. New aspects unlock at
          // highestVaultTier 2/3/4 via the gameplay loop; this migration
          // only seeds the baseline state.
          const sp = state.player as Player;
          if (!sp.aspects) {
            sp.aspects = {
              primary: 'bulwark',
              secondary: null,
              levels: { bulwark: 1, pyromant: 0, stormcaller: 0, vampire: 0, phantom: 0, engineer: 0 },
              unlocked: ['bulwark'],
            };
          }
          // Retroactively unlock aspects the player has already EARNED by
          // virtue of past Vault-clears. Doesn't auto-slot — player still
          // picks which aspect goes into primary/secondary.
          const tier = sp.highestVaultTier ?? 0;
          const unlocked = new Set(sp.aspects.unlocked);
          if (tier >= 2) unlocked.add('pyromant');
          if (tier >= 3) unlocked.add('stormcaller');
          if (tier >= 4) unlocked.add('vampire');
          sp.aspects.unlocked = Array.from(unlocked);
          if (!sp.unlockedActiveSkills) {
            sp.unlockedActiveSkills = ['frost_nova'];
          }
          if (sp.activeSkillId === undefined) {
            sp.activeSkillId = 'frost_nova';
          }
          state.player = sp;
        }
        if (version < 12) {
          // P2.A.6 Decrypter + Heat SUNSET. The Decrypter network and Clean
          // Code currency are removed; everything refunds to Corrupted Data
          // so the player keeps their investment value:
          //  - cleanCode (current balance)        → CD += cleanCode * 0.5
          //  - decrypters[id].buffer (each)       → CD += buffer * 1.0
          //  - decrypters[id].level Lv2+ cost     → CD += 50% of historic
          //                                          purchase cost (summed)
          //  - decrypters[id].storageMultiplier=2 → 50 Neon Gems back per
          //                                          purchased doubling
          //
          // Heat / overheated lockout simply evaporate — no compensation
          // since they were time-gated rate-limits, not investments.
          const sp = state.player as Player & {
            cleanCode?: number;
            heat?: number;
            overheatedUntil?: number;
            decrypters?: Record<string, { level?: number; buffer?: number; storageMultiplier?: number }>;
          };
          let refundedCd = 0;
          let refundedGems = 0;
          const oldCleanCode = sp.cleanCode ?? 0;
          if (oldCleanCode > 0) refundedCd += Math.floor(oldCleanCode * 0.5);
          if (sp.decrypters) {
            for (const dec of Object.values(sp.decrypters)) {
              if (dec.buffer && dec.buffer > 0) refundedCd += Math.floor(dec.buffer);
              // Approximate refund of level-purchase cost: starting Lv 1 is free
              // (the seeded Scrap-Parser), every Lv above 1 cost increasing CC.
              // Use a flat per-level estimate of 100 CD-equivalent because we
              // don't have the historic price-curve handy — keeps refund honest
              // without parsing the deleted decrypters.ts cost table.
              if (dec.level && dec.level > 1) {
                refundedCd += (dec.level - 1) * 100;
              }
              // Each storage-doubling cost 50 Neon Gems and is gone now.
              if (dec.storageMultiplier && dec.storageMultiplier > 1) {
                refundedGems += 50 * (dec.storageMultiplier - 1);
              }
            }
          }
          if (refundedCd > 0) {
            sp.corruptedData = (sp.corruptedData ?? 0) + refundedCd;
          }
          if (refundedGems > 0) {
            sp.neonGems = (sp.neonGems ?? 0) + refundedGems;
          }
          // Wipe the legacy fields. TypeScript-level shape removal happens in
          // a follow-up commit (Player type still carries them as optional for
          // backward-compat during the transition window).
          sp.cleanCode = 0;
          sp.heat = 0;
          sp.overheatedUntil = 0;
          delete sp.decrypters;
          state.player = sp;
        }

        return state;
      },
      partialize: (state) => ({ player: state.player }),
    },
  ),
);

if (typeof window !== 'undefined') {
  (window as unknown as { useGameStore?: typeof useGameStore; instanceStats?: typeof instanceStats }).useGameStore = useGameStore;
  (window as unknown as { instanceStats?: typeof instanceStats }).instanceStats = instanceStats;
}
